// Die Kiste im Client (task-014, ADR 0031). Kiste und Leiste hält der Server; der Client zeigt die
// sechzehn Fächer aus `hello.chest` und den `chest`-Nachrichten und schickt einen Klick als
// `chestMove`. Aufgehen tut die Ansicht erst, wenn der Server „Öffnen“ bestätigt hat – so zeigt sie
// nie eine fremde Kiste und nie eine, vor der man gar nicht steht.
//
// Wem gehört eine Antwort (`actionResult`)? Der Server beantwortet `interact` und `chestMove` gleich.
// Die Kiste merkt sich, worauf sie wartet – das Öffnen oder eine Verschiebung –, und sagt es beim
// Eintreffen. Alles andere gehört der Interaktionstaste (game/interaktion.ts).
//
// Reine Logik ohne DOM; main.ts reicht Zeit, Nachrichten und die Leiste herein, hud/kistenansicht.ts
// zeichnet.
import {
  KISTE_FAECHER,
  PROTOCOL_VERSION,
  leereKiste,
  type ActionResultMessage,
  type ChestMoveMessage,
  type ChestSide,
  type ItemStack,
  type Kistenfach,
} from '@bauernhof/shared';
import { ANTWORT_WARTEZEIT_MS, STANDARD_ABLEHNUNG } from './interaktion.js';

/** Was eine Antwort des Servers für die Kiste bedeutet. */
export type KistenAntwort =
  /** Die Antwort auf „Öffnen“: Geht sie auf? */
  | { art: 'oeffnung'; auf: boolean }
  /** Die Antwort auf einen Klick; `meldung` ist der Satz einer Ablehnung, sonst null. */
  | { art: 'verschiebung'; meldung: string | null };

export interface KistenZustand {
  /** Die sechzehn Fächer, wie der Server sie zuletzt nannte. */
  faecher(): readonly Kistenfach[];
  /** Übernimmt die Fächer aus `hello.chest` oder einer `chest`-Nachricht, als eigene Kopie. */
  setzeFaecher(slots: readonly Kistenfach[]): void;
  istOffen(): boolean;
  /** „Öffnen [E]“ an der eigenen Kiste ging hinaus; die nächste Antwort entscheidet, ob sie aufgeht. */
  oeffnetGleich(jetzt: number): void;
  /**
   * Ein Klick auf ein Fach. Liefert die Nachricht an den Server – oder null, wenn die Kiste zu ist,
   * im Fach nichts liegt oder die Antwort auf den vorigen Klick noch aussteht (höchstens
   * `ANTWORT_WARTEZEIT_MS`). `stacks` ist die Leiste, wie sie gerade gezeigt wird.
   */
  klick(from: ChestSide, index: number, stacks: readonly ItemStack[], jetzt: number): ChestMoveMessage | null;
  /** Die Antwort des Servers; null, wenn die Kiste auf keine wartet – dann gehört sie der Interaktion. */
  antwort(msg: ActionResultMessage, jetzt: number): KistenAntwort | null;
  /** Escape, E oder „Schließen“. */
  schliesse(): void;
  /** Nach einer neuen Verbindung: zu, und keine Antwort steht mehr aus – der Server hat die Kiste auch zu. */
  vergiss(): void;
}

export function createKiste(): KistenZustand {
  let faecher: Kistenfach[] = leereKiste();
  let offen = false;
  /** Worauf die Kiste wartet und seit wann; null, wenn nichts unterwegs ist. */
  let unterwegs: { art: KistenAntwort['art']; seit: number } | null = null;
  const wartet = (jetzt: number): boolean => unterwegs !== null && jetzt - unterwegs.seit < ANTWORT_WARTEZEIT_MS;

  return {
    faecher: () => faecher,

    setzeFaecher(slots) {
      faecher = slots.map((fach) => (fach === null ? null : { ...fach }));
    },

    istOffen: () => offen,

    oeffnetGleich(jetzt) {
      unterwegs = { art: 'oeffnung', seit: jetzt };
    },

    klick(from, index, stacks, jetzt) {
      if (!offen || wartet(jetzt) || !Number.isInteger(index) || index < 0) return null;
      const belegt = from === 'kiste' ? index < KISTE_FAECHER && (faecher[index] ?? null) !== null : index < stacks.length;
      if (!belegt) return null;
      unterwegs = { art: 'verschiebung', seit: jetzt };
      return { v: PROTOCOL_VERSION, t: 'chestMove', from, index };
    },

    antwort(msg, jetzt) {
      const erwartet = wartet(jetzt) ? unterwegs : null;
      unterwegs = null;
      if (erwartet === null) return null;
      if (erwartet.art === 'oeffnung') {
        offen = msg.ok;
        return { art: 'oeffnung', auf: msg.ok };
      }
      if (msg.ok) return { art: 'verschiebung', meldung: null };
      return { art: 'verschiebung', meldung: msg.message !== undefined && msg.message !== '' ? msg.message : STANDARD_ABLEHNUNG };
    },

    schliesse() {
      offen = false;
    },

    vergiss() {
      offen = false;
      unterwegs = null;
    },
  };
}
